import React, { useCallback, lazy, memo } from 'react';
import { MinusIcon, PlusIcon, TrashIcon, HeartIcon, FileTextIcon, GlobeIcon, PackageIcon, GiftIcon, LayersIcon, InfoIcon, SnowflakeIcon, ZapIcon } from 'lucide-react';
import { motion, useAnimation, PanInfo } from 'framer-motion';
export type CartItemType = 'single' | 'double' | 'discount' | 'prescription' | 'global' | 'bundle' | 'gift' | 'combo';
export interface CartSubItem {
  id: number;
  name: string;
  image: string;
  quantity?: number;
}
export interface CartItemData {
  id: number;
  name: string;
  price: number;
  originalPrice?: number;
  quantity: number;
  image: string;
  type: CartItemType;
  requiresPrescription?: boolean;
  isGlobal?: boolean;
  bundleItems?: CartSubItem[];
  giftItem?: CartSubItem;
  comboItems?: CartSubItem[];
  brand?: string;
  coldChain?: boolean;
  nowEligible?: boolean;
  temperatureRange?: string;
}
interface CartItemProps {
  item: CartItemData;
  onQuantityChange: (id: number, quantity: number) => void;
  onRemove: (id: number) => void;
  onSaveForLater?: (id: number) => void;
}
export const CartItem = memo(function CartItem({
  item,
  onQuantityChange,
  onRemove,
  onSaveForLater
}: CartItemProps) {
  const controls = useAnimation();
  const handleDecrease = useCallback(() => {
    if (item.quantity > 1) {
      onQuantityChange(item.id, item.quantity - 1);
    }
  }, [item.id, item.quantity, onQuantityChange]);
  const handleIncrease = useCallback(() => {
    onQuantityChange(item.id, item.quantity + 1);
  }, [item.id, item.quantity, onQuantityChange]);
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || 1;
    onQuantityChange(item.id, value);
  }, [item.id, onQuantityChange]);
  const handleRemove = useCallback(() => {
    onRemove(item.id);
  }, [item.id, onRemove]);
  const handleDragEnd = (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const offset = info.offset.x;
    const velocity = info.velocity.x;
    if (offset < -50 || velocity < -500) {
      controls.start({
        x: -80
      });
    } else {
      controls.start({
        x: 0
      });
    }
  };
  const handleSwipeDelete = () => {
    controls.start({
      x: -window.innerWidth,
      opacity: 0
    }).then(() => {
      handleRemove();
    });
  };
  const totalPrice = (item.price * item.quantity).toFixed(2);
  const hasDiscount = item.originalPrice && item.originalPrice > item.price;
  return <div className="relative rounded-xl border border-gray-200 overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow" data-id="element-1415" data-mp="2ioj98b7uy4nw">
      {/* Background Delete Action (Mobile Only) */}
      <div className="absolute inset-y-0 right-0 w-20 flex items-center justify-center md:hidden bg-red-500" data-id="element-1416" data-mp="28t7tbpkpqrek">
        <button onClick={handleSwipeDelete} className="w-full h-full flex flex-col items-center justify-center text-white" aria-label="Delete item" data-id="element-1417" data-mp="byldgdcj2pg2">
          <TrashIcon className="w-6 h-6 mb-1" data-id="element-1418" data-mp="ej9lu4xyk0fm" />
          <span className="text-xs font-medium" data-id="element-1419" data-mp="jimbsyk4lnmf">Delete</span>
        </button>
      </div>

      {/* Foreground Content */}
      <motion.div drag="x" dragConstraints={{
      left: -80,
      right: 0
    }} dragElastic={0.1} onDragEnd={handleDragEnd} animate={controls} className="relative z-10 bg-white p-3 md:p-5" data-id="element-1420" data-mp="oanim4yiym52">
        <div className="flex flex-col md:flex-row gap-4" data-id="element-1421" data-mp="k1ntfdj6yd0">
          {/* Product Image & Main Info */}
          <div className="flex gap-4 flex-1" data-id="element-1422" data-mp="5j4kabfg3w9d">
            <div className="relative flex-shrink-0 w-24 h-24 md:w-32 md:h-32 bg-gray-50 rounded-xl overflow-hidden border border-gray-100" data-id="element-1423" data-mp="5ibkjuewm70q">
              <img src={item.image} alt={item.name} className="w-full h-full object-cover" loading="lazy" data-id="element-1424" data-mp="afuz6vfiklt7" />
              {item.isGlobal && <div className="absolute top-1 left-1 bg-blue-600 text-white p-1 rounded-lg shadow-sm" data-id="element-1425" data-mp="1ji68cklh6gdv">
                  <GlobeIcon className="w-3 h-3" data-id="element-1426" data-mp="1fhmfvubuk0y2" />
                </div>}
            </div>

            <div className="flex-1 min-w-0 flex flex-col" data-id="element-1427" data-mp="1th5iy8pswm3i">
              <div className="flex flex-wrap gap-2 mb-1.5" data-id="element-1428" data-mp="1tej37lsrb0a7">
                {item.requiresPrescription && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[10px] font-bold border border-amber-100 uppercase tracking-wider" data-id="element-1429" data-mp="22hk6leq8mda6">
                    <FileTextIcon className="w-3 h-3" data-id="element-1430" data-mp="2c7diyhy7ewfh" />
                    Prescription Required
                  </span>}
                {item.coldChain && <span className="inline-flex items-center gap-1 rounded-full border border-[#b8e2df] bg-[#edf9f8] px-2 py-0.5 text-[10px] font-bold text-[#176b66] uppercase tracking-wider" data-id="element-1431" data-mp="27925lx8pa3si">
                    <SnowflakeIcon className="h-3 w-3" data-id="element-1432" data-mp="2h8azyr7h9kfe" />
                    Chilled · {item.temperatureRange || '2–8°C'}
                  </span>}
                {!item.coldChain && item.nowEligible && <span className="inline-flex items-center gap-1 rounded-full border border-blue-100 bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-700 uppercase tracking-wider" data-id="element-1433" data-mp="34rinprz0mxc1">
                    <ZapIcon className="h-3 w-3" data-id="element-1434" data-mp="2vv9nib37ruou" />
                    120 min eligible
                  </span>}
                {item.type === 'bundle' && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 text-[10px] font-bold border border-purple-100 uppercase tracking-wider" data-id="element-1435" data-mp="2lwk3abnuhy5f">
                    <PackageIcon className="w-3 h-3" data-id="element-1436" data-mp="2d373qqv9zyad" />
                    Bundle Deal
                  </span>}
                {item.type === 'combo' && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-[10px] font-bold border border-indigo-100 uppercase tracking-wider" data-id="element-1437" data-mp="234rdeq42ot1x">
                    <LayersIcon className="w-3 h-3" data-id="element-1438" data-mp="1f8wczlh1f2oy" />
                    Combo Set
                  </span>}
                {item.type === 'double' && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-[10px] font-bold border border-green-100 uppercase tracking-wider" data-id="element-1439" data-mp="15b05r4r80rw4">
                    Buy 1 Get 1
                  </span>}
              </div>

              <h3 className="text-sm md:text-lg font-bold text-gray-900 line-clamp-2 leading-tight mb-1" data-id="element-1440" data-mp="1xxt8fso6818r">
                {item.name}
              </h3>
              {item.brand && <p className="text-xs text-gray-500 mb-2" data-id="element-1441" data-mp="1o184lanw0w6i">{item.brand}</p>}

              <div className="mt-auto flex items-center gap-3" data-id="element-1442" data-mp="hmyxzy1v9fhr">
                <div className="flex flex-col" data-id="element-1443" data-mp="cm1ixuc5l1kh">
                  <div className="flex items-center gap-2" data-id="element-1444" data-mp="coff66ikkjtm">
                    <span className="text-base md:text-xl font-black text-[#1E2B55]" data-id="element-1445" data-mp="2oapktzlhbsuu">
                      {item.price.toFixed(2)} SAR
                    </span>
                    {hasDiscount && <span className="text-xs md:text-sm text-gray-400 line-through font-medium" data-id="element-1446" data-mp="2y72dqv97a2oj">
                        {item.originalPrice?.toFixed(2)} SAR
                      </span>}
                  </div>
                  {hasDiscount && <span className="text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded w-fit mt-0.5" data-id="element-1447" data-mp="3bypmhukbx3o7">
                      Save {(item.originalPrice! - item.price).toFixed(2)} SAR
                    </span>}
                </div>
              </div>
            </div>
          </div>

          {/* Controls & Actions */}
          <div className="flex flex-row md:flex-col items-center md:items-end justify-between md:justify-start gap-4 pt-3 md:pt-0 border-t md:border-t-0 border-gray-100" data-id="element-1448" data-mp="1mm5bo4m608na">
            <div className="flex flex-col md:items-end gap-1" data-id="element-1449" data-mp="3tfmskja04jy2">
              <div className="flex items-center bg-gray-50 rounded-xl p-1 border border-gray-200" data-id="element-1450" data-mp="3oi4eyfl1hgjq">
                <button onClick={handleDecrease} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white hover:shadow-sm transition-all disabled:opacity-30" disabled={item.quantity <= 1} data-id="element-1451" data-mp="713dslh472r2">
                  <MinusIcon className="w-4 h-4 text-gray-600" data-id="element-1452" data-mp="3ajwmajv1d1ed" />
                </button>
                <input type="number" value={item.quantity} onChange={handleInputChange} className="w-10 text-center bg-transparent font-bold text-sm focus:outline-none" min="1" data-id="element-1453" data-mp="35l378n03p2c3" />
                <button onClick={handleIncrease} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white hover:shadow-sm transition-all" data-id="element-1454" data-mp="zo7dqc1coqvz">
                  <PlusIcon className="w-4 h-4 text-gray-600" data-id="element-1455" data-mp="12ct6stp0b1hj" />
                </button>
              </div>
              <p className="hidden md:block text-[10px] text-gray-400 font-medium mt-1" data-id="element-1456" data-mp="1776x4hotaifc">
                Unit Price: {item.price.toFixed(2)} SAR
              </p>
            </div>

            <div className="flex items-center gap-2" data-id="element-1457" data-mp="xdg8ncsoq7zn">
              <button onClick={handleRemove} className="p-2.5 rounded-xl border border-gray-200 text-gray-400 hover:text-red-600 hover:border-red-200 hover:bg-red-50 transition-all group" title="Remove item" data-id="element-1458" data-mp="1l0oebvu0zj27">
                <TrashIcon className="w-5 h-5 group-active:scale-90 transition-transform" data-id="element-1459" data-mp="1g9zmugicim32" />
              </button>
            </div>

            <div className="hidden md:block mt-auto text-right" data-id="element-1460" data-mp="23yr8ep1fk66v">
              <p className="text-xs text-gray-400 font-medium mb-0.5" data-id="element-1461" data-mp="28qabkdqu7o7k">Total</p>
              <p className="text-xl font-black text-[#1E2B55]" data-id="element-1462" data-mp="28sdbpqlpexq0">
                {totalPrice} SAR
              </p>
            </div>
          </div>
        </div>

        {/* Sub-items (Bundles, Combos, Gifts) */}
        {(item.bundleItems || item.comboItems || item.giftItem) && <div className="mt-4 pt-4 border-t border-dashed border-gray-200" data-id="element-1463" data-mp="nar7v6hjmq7r">
            <div className="flex items-center gap-2 mb-3" data-id="element-1464" data-mp="s8so7jnl4bfj">
              <InfoIcon className="w-4 h-4 text-gray-400" data-id="element-1465" data-mp="eiyykz499n4r" />
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-1466" data-mp="ehe6dg5eyga2">
                Included in this {item.type === 'gift' ? 'order' : 'set'}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-1467" data-mp="4mccy843us1o">
              {item.bundleItems?.map(sub => <div key={sub.id} className="flex items-center gap-3 bg-gray-50 p-2 rounded-lg border border-gray-100" data-id="element-1468" data-mp="3rph9b11ni9on">
                  <img src={sub.image} alt={sub.name} className="w-10 h-10 rounded object-cover bg-white" data-id="element-1469" data-mp="3vsx55v5wc60a" />
                  <div className="min-w-0" data-id="element-1470" data-mp="3859xqzy35161">
                    <p className="text-[11px] font-medium text-gray-700 truncate" data-id="element-1471" data-mp="3m3q4urzv0top">
                      {sub.name}
                    </p>
                    <p className="text-[10px] text-gray-400" data-id="element-1472" data-mp="3h2kayqugr0dl">
                      Qty: {sub.quantity || 1}
                    </p>
                  </div>
                </div>)}

              {item.comboItems?.map(sub => <div key={sub.id} className="flex items-center gap-3 bg-gray-50 p-2 rounded-lg border border-gray-100" data-id="element-1473" data-mp="8oajos3rnaag">
                  <img src={sub.image} alt={sub.name} className="w-10 h-10 rounded object-cover bg-white" data-id="element-1474" data-mp="4otf00w5yh6c" />
                  <div className="min-w-0" data-id="element-1475" data-mp="doy8r88fph21">
                    <p className="text-[11px] font-medium text-gray-700 truncate" data-id="element-1476" data-mp="37fmxnzk7f5wq">
                      {sub.name}
                    </p>
                    <p className="text-[10px] text-gray-400" data-id="element-1477" data-mp="3bhrokqj9pofo">
                      Included in Combo
                    </p>
                  </div>
                </div>)}

              {item.giftItem && <div className="flex items-center gap-3 bg-green-50 p-2 rounded-lg border border-green-100 col-span-full sm:col-span-1" data-id="element-1478" data-mp="3l8uohp0gnxdd">
                  <div className="relative" data-id="element-1479" data-mp="3v4pex6axewqp">
                    <img src={item.giftItem.image} alt={item.giftItem.name} className="w-10 h-10 rounded object-cover bg-white" data-id="element-1480" data-mp="3v3e8szu0dcwf" />
                    <div className="absolute -top-1 -right-1 bg-green-500 text-white p-0.5 rounded-full shadow-sm" data-id="element-1481" data-mp="7y5e04hc1ogk">
                      <GiftIcon className="w-2.5 h-2.5" data-id="element-1482" data-mp="29rmjp2lwkf8o" />
                    </div>
                  </div>
                  <div className="min-w-0" data-id="element-1483" data-mp="2283lvq802hg1">
                    <div className="flex items-center gap-1" data-id="element-1484" data-mp="228vdyr2e1x1k">
                      <span className="text-[9px] font-black text-green-600 uppercase tracking-tighter" data-id="element-1485" data-mp="1xhn2lphi01lr">
                        Free Gift
                      </span>
                    </div>
                    <p className="text-[11px] font-bold text-gray-800 truncate" data-id="element-1486" data-mp="1njh47huwhdln">
                      {item.giftItem.name}
                    </p>
                  </div>
                </div>}
            </div>
          </div>}
      </motion.div>
    </div>;
});